import { appConfig } from '@common/config';
import { useMutation, useQuery, type UseQueryOptions, type UseQueryResult } from '@tanstack/react-query';
import { publicApi } from './remote';

export type QueryParams = Record<string, any>;
export type MutateBody = Record<string, any> | any;

export type ReactQueryOptions<TQueryFnData = unknown, TError = unknown> =
    | Omit<UseQueryOptions<TQueryFnData, TError>, 'queryKey' | 'queryFn'>
    | undefined;

export const fetchData = async (url: string, params?: QueryParams): Promise<any> => {
    const res = await publicApi.get<UseQueryResult>(url, { params });
    return res.data;
};

export const mutatePost = async (url: string, data?: MutateBody): Promise<any> => {
    const res = await publicApi.post(url, data, { withCredentials: true });
    return res.data;
};

export const mutatePut = async (url: string, data?: MutateBody): Promise<any> => {
    const res = await publicApi.put(url, data, { withCredentials: true });
    return res.data;
};

export const mutateDelete = async (url: string): Promise<any> => {
    const res = await publicApi.delete(url, { withCredentials: true });
    return res.data;
};

/* 
    Data Query
*/
export const useDataQuery = <T>(
    queryKeyFn: (path: string, params: QueryParams) => Array<string | QueryParams>,
    path: string,
    params: QueryParams,
    queryOptions?: ReactQueryOptions<T>
) => {
    return useQuery({
        retry: 2,
        queryKey: queryKeyFn(path, params),
        queryFn: () => fetchData(path, { ...params, locale: appConfig.locale }),
        ...queryOptions,
    });
};

export const useMutate = () => {
    return useMutation({
        mutationFn: ({ method, apiPath, data }: { method: 'post' | 'put' | 'delete'; apiPath: string; data: MutateBody }) => {
            if (method === 'post') {
                return mutatePost(apiPath, data);
            }
            if (method === 'put') {
                return mutatePut(apiPath, data);
            }
            if (method === 'delete') {
                return mutateDelete(apiPath);
            }
            return mutatePost(apiPath, data);
        },
    });
};
